import { createFileRoute } from "@tanstack/react-router";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { AuthLoading } from "@/components/auth-loading";
import { AUTH_API_URL } from "@/lib/auth-client";

export const Route = createFileRoute("/verify-email")({
  validateSearch: z.object({
    token: z.string().optional(),
    callbackURL: z.string().optional(),
    error: z.string().optional(),
  }).parse,
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { token, callbackURL, error } = Route.useSearch();
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      setLocalError("This verification link is no longer valid. Sign in to request a new one.");
      return;
    }

    if (!token) {
      setLocalError(
        "This verification link is missing its token. Request a new verification email.",
      );
      return;
    }

    const verifyUrl = new URL("/api/auth/verify-email", AUTH_API_URL);
    verifyUrl.searchParams.set("token", token);
    if (callbackURL) verifyUrl.searchParams.set("callbackURL", callbackURL);
    window.location.replace(verifyUrl.toString());
  }, [callbackURL, error, token]);

  if (localError) {
    return (
      <div className="space-y-6">
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight">Verification link issue</h2>
          <p className="text-sm text-muted-foreground">We couldn't finish email verification.</p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{localError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return <AuthLoading label="Verifying your email…" data-testid="verify-email-loading" />;
}
